import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../../context/AppState";
import {
  isCustomCartDrawerThemeUpsellInjectMode,
  THEME_CART_UPSELL_HOST_ID,
} from "../../constants/custom-cart-drawer-template";
import RenderThemeCartUpsellSlot from "./RenderThemeCartUpsellSlot";

const PREVIEW_CSS_URL = "https://cdn.weupsell.com/static/css/main.css";

function ensureShadowContent(host) {
  if (!host) return null;
  const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });

  if (!shadow.querySelector("link[data-weupsell-theme-cart-upsell-css]")) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PREVIEW_CSS_URL;
    link.setAttribute("data-weupsell-theme-cart-upsell-css", "true");
    shadow.appendChild(link);
  }

  if (!shadow.querySelector("style[data-weupsell-theme-cart-upsell-base]")) {
    const style = document.createElement("style");
    style.setAttribute("data-weupsell-theme-cart-upsell-base", "true");
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }
      #weupsell-theme-cart-upsell-content {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }
    `;
    shadow.appendChild(style);
  }

  let content = shadow.querySelector("#weupsell-theme-cart-upsell-content");
  if (!content) {
    content = document.createElement("div");
    content.id = "weupsell-theme-cart-upsell-content";
    shadow.appendChild(content);
  }

  Object.assign(host.style, {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  });

  return content;
}

/**
 * When custom cart drawer replace is off, mount upsell into merchant host div.
 */
export default function WeupsellThemeCartUpsellRuntime({ popupValues = [] }) {
  const { appState, isMobile: viewportIsMobile } = useApp();
  const injectPopups = useMemo(
    () =>
      (Array.isArray(popupValues) ? popupValues : []).filter(
        (popup) => popup && isCustomCartDrawerThemeUpsellInjectMode(popup)
      ),
    [popupValues]
  );
  const [portal, setPortal] = useState(null);

  useEffect(() => {
    if (appState?.is_preview_mode || !injectPopups.length) {
      setPortal(null);
      return undefined;
    }

    let rafId = null;
    let isDisposed = false;
    const popup = injectPopups[0];

    const processHost = () => {
      if (isDisposed) return;
      const host = document.getElementById(THEME_CART_UPSELL_HOST_ID);
      if (!host) {
        setPortal(null);
        return;
      }
      const content = ensureShadowContent(host);
      if (!content) {
        setPortal(null);
        return;
      }
      const nextId = `weupsell-theme-cart-upsell-${popup._id || "static"}`;
      setPortal((prev) => {
        if (
          prev &&
          prev.id === nextId &&
          prev.content === content &&
          prev.popup === popup
        ) {
          return prev;
        }
        return {
          id: nextId,
          content,
          popup,
        };
      });
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processHost();
      });
    };

    processHost();

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id"],
    });

    return () => {
      isDisposed = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      observer.disconnect();
      setPortal(null);
    };
  }, [appState?.is_preview_mode, injectPopups]);

  if (appState?.is_preview_mode || !portal) return null;

  return createPortal(
    <RenderThemeCartUpsellSlot
      popupData={portal.popup}
      isMobile={viewportIsMobile}
    />,
    portal.content,
    portal.id
  );
}
